/**
 * AdminDestinationCardsPanel — Iter 79.
 *
 * Manual editor for the homepage "Where renters love to drive" section.
 * Admin adds/edits/reorders destination cards (title, country, excerpt,
 * image upload or URL, link, visibility). When at least one active card
 * exists, the homepage renders these instead of the CMS/built-in fallback.
 */
import { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, ImagePlus, Loader2, Pencil, Trash2, Upload, Plus, ImageIcon } from "lucide-react";

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

function _newDraft() {
  return { title: "", country: "Albania", excerpt: "", image_path: "", link_url: "", position: 0, is_active: true };
}

export default function AdminDestinationCardsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(_newDraft());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/destination-cards");
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load destination cards.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onOpenCreate = () => { setEditing(null); setDraft({ ..._newDraft(), position: rows.length }); setDialogOpen(true); };
  const onOpenEdit = (r) => {
    setEditing(r);
    setDraft({
      title: r.title || "", country: r.country || "Albania", excerpt: r.excerpt || "",
      image_path: r.image_path || "", link_url: r.link_url || "",
      position: r.position ?? 0, is_active: r.is_active !== false,
    });
    setDialogOpen(true);
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Please pick an image file."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, image_path: data.path }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSave = async () => {
    if (!draft.title.trim()) { toast.error("Title is required."); return; }
    if (!draft.image_path.trim()) { toast.error("Image is required."); return; }
    setSaving(true);
    try {
      const body = {
        title: draft.title.trim(),
        country: (draft.country || "Albania").trim(),
        excerpt: (draft.excerpt || "").trim() || null,
        image_path: draft.image_path.trim(),
        link_url: (draft.link_url || "").trim() || null,
        position: Number(draft.position || 0),
        is_active: !!draft.is_active,
      };
      if (editing) {
        await api.patch(`/admin/destination-cards/${editing.id}`, body);
        toast.success("Destination updated.");
      } else {
        await api.post("/admin/destination-cards", body);
        toast.success("Destination added.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Remove "${r.title}" from the Top Destinations section?`)) return;
    try {
      await api.delete(`/admin/destination-cards/${r.id}`);
      toast.success("Destination removed.");
      load();
    } catch (err) { toast.error(formatApiError(err) || "Delete failed."); }
  };

  const onToggleActive = async (r) => {
    try {
      await api.patch(`/admin/destination-cards/${r.id}`, { is_active: !r.is_active });
      load();
    } catch (err) { toast.error(formatApiError(err) || "Update failed."); }
  };

  return (
    <div className="space-y-6" data-testid="destination-cards-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-medium">Top Destinations ("Where renters love to drive")</h3>
          <p className="text-xs text-neutral-500 max-w-2xl">
            Manually curate the homepage destination cards. When at least one active card exists, the section shows
            exactly these; otherwise it falls back to featured destination blog posts, then the built-in city set.
            Link can be internal (e.g. <code className="font-mono bg-neutral-100 px-1 rounded">/car-rental/sarande-albania</code>) or a full URL.
          </p>
        </div>
        <Button onClick={onOpenCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="destination-card-add">
          <ImagePlus className="h-4 w-4 mr-1.5" /> Add destination
        </Button>
      </div>

      {loading && (
        <div className="rounded-2xl border border-border bg-white p-10 text-center text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center">
          <ImageIcon className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
          <div className="text-sm font-medium text-neutral-700">No custom destinations yet</div>
          <div className="text-xs text-neutral-500 mt-1 mb-4">The homepage currently shows the built-in fallback cards. Add your own to take control.</div>
          <Button onClick={onOpenCreate} className="bg-emerald-600 hover:bg-emerald-700">
            <ImagePlus className="h-4 w-4 mr-1.5" /> Add first destination
          </Button>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((r) => (
            <div
              key={r.id}
              className={`rounded-xl border border-border overflow-hidden bg-white transition ${r.is_active ? "" : "opacity-50"}`}
              data-testid={`destination-card-${r.id}`}
            >
              <div className="relative aspect-[4/3] bg-neutral-200 overflow-hidden">
                <img src={_resolveSrc(r.image_path)} alt={r.title} className="w-full h-full object-cover" />
                <div className="absolute top-2 left-2 flex items-center gap-1">
                  <Badge className="bg-black/60 text-white text-[10px]">#{r.position || 0}</Badge>
                  {!r.is_active && <Badge variant="secondary" className="text-[10px]">Hidden</Badge>}
                </div>
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
                  <MapPin className="h-3 w-3" /> {r.country || "Albania"}
                </div>
                <div className="font-semibold text-sm truncate">{r.title}</div>
                <div className="text-xs text-neutral-500 line-clamp-2 min-h-[2rem]">
                  {r.excerpt || <span className="italic text-neutral-400">No description</span>}
                </div>
                <div className="flex items-center justify-between gap-1 pt-1 border-t border-border">
                  <Switch
                    checked={!!r.is_active}
                    onCheckedChange={() => onToggleActive(r)}
                    className="data-[state=checked]:bg-emerald-600"
                    data-testid={`destination-card-toggle-${r.id}`}
                  />
                  <div className="flex items-center gap-0.5">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenEdit(r)} data-testid={`destination-card-edit-${r.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => onDelete(r)} data-testid={`destination-card-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="destination-card-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-emerald-600" />
              {editing ? "Edit destination" : "Add destination"}
            </DialogTitle>
            <DialogDescription>Shown in the homepage "Where renters love to drive" grid (max 6 visible, ordered by position).</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Sarandë" className="mt-1" data-testid="destination-dialog-title" />
              </div>
              <div>
                <Label>Country label</Label>
                <Input value={draft.country} onChange={(e) => setDraft({ ...draft, country: e.target.value })} placeholder="Albania" className="mt-1" data-testid="destination-dialog-country" />
              </div>
            </div>

            <div>
              <Label>Description (shown under the title)</Label>
              <Textarea value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} rows={2} maxLength={300} placeholder="e.g. Gateway to the Albanian Riviera — turquoise coves and Ksamil islands." className="mt-1" data-testid="destination-dialog-excerpt" />
            </div>

            <div>
              <Label>Image</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  value={draft.image_path}
                  onChange={(e) => setDraft({ ...draft, image_path: e.target.value })}
                  placeholder="Paste image URL or upload"
                  className="flex-1"
                  data-testid="destination-dialog-image"
                />
                <Button variant="outline" type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="destination-dialog-upload">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Upload</>}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" />
              </div>
              {draft.image_path && (
                <div className="mt-2 rounded-lg overflow-hidden border border-border bg-neutral-50 aspect-[4/3] max-h-44" data-testid="destination-dialog-preview">
                  <img src={_resolveSrc(draft.image_path)} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Link (optional)</Label>
                <Input value={draft.link_url} onChange={(e) => setDraft({ ...draft, link_url: e.target.value })} placeholder="/car-rental/sarande-albania" className="mt-1" data-testid="destination-dialog-link" />
              </div>
              <div>
                <Label>Position</Label>
                <Input type="number" step="1" min="0" value={draft.position} onChange={(e) => setDraft({ ...draft, position: e.target.value })} className="mt-1 tabular-nums" data-testid="destination-dialog-position" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={!!draft.is_active}
                onCheckedChange={(v) => setDraft({ ...draft, is_active: !!v })}
                className="data-[state=checked]:bg-emerald-600"
                data-testid="destination-dialog-active"
              />
              <Label className="cursor-pointer" onClick={() => setDraft({ ...draft, is_active: !draft.is_active })}>
                Visible on homepage
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={onSave} disabled={saving || !draft.title.trim() || !draft.image_path.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="destination-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : (editing ? <Pencil className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />)}
              {editing ? "Save changes" : "Add destination"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
